/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  Search as SearchIcon 
} from '@mui/icons-material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import useFacility from '../../hooks/useFacility';
import { toast } from 'react-toastify';

const SubstrateManagement = () => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  const [substrates, setSubstrates] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSubstrate, setEditingSubstrate] = useState(null);
  const [newSubstrate, setNewSubstrate] = useState({
    name: '',
    description: '',
    facility: ''
  });

  // Pagination and search states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSubstrates, setFilteredSubstrates] = useState([]);

  // Sorting hook
  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredSubstrates,
    { defaultSortColumn: 'name', defaultSortOrder: 'asc' }
  );

  useEffect(() => {
    fetchData();
  }, [selectedFacility]);

  useEffect(() => {
    const filtered = substrates.filter(substrate => 
      substrate.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      substrate.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      substrate.facility?.facility_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredSubstrates(filtered);
    setPage(0);
  }, [searchQuery, substrates]);

  const fetchData = async () => {
    try {
      const [substratesRes, facilitiesRes] = await Promise.all([
        axios.get('/api/substrates'),
        axios.get('/api/facilities')
      ]);
      
      let filteredSubstrates = substratesRes.data;
      if (selectedFacility !== 'all') {
        filteredSubstrates = filteredSubstrates.filter(
          substrate => substrate.facility._id === selectedFacility
        );
      }
      
      setSubstrates(filteredSubstrates);
      setFacilities(facilitiesRes.data);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch data');
      setLoading(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate pagination values
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
  const paginatedSubstrates = sortedData.slice(startIndex, endIndex);

  // CRUD operations
  const handleCreate = async () => {
    try {
      
      if (!newSubstrate.name || !newSubstrate.description || !newSubstrate.facility) {
        alert('Please fill in all required fields');
        return;
      }

      await axios.post('/api/substrates', newSubstrate);
      
      setNewSubstrate({ name: '', description: '', facility: '' });
      fetchData();
      toast.success('Substrate created successfully');
    } catch (error) {
      console.error('Error creating substrate:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Error creating substrate');
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/api/substrates/${editingSubstrate._id}`, editingSubstrate);
      setEditDialogOpen(false);
      setEditingSubstrate(null);
      fetchData();
    } catch (error) {
      console.error('Error updating substrate:', error);
      alert(error.response?.data?.error || 'Error updating substrate');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this substrate?')) {
      try {
        await axios.delete(`/api/substrates/${id}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete substrate');
      }
    }
  };

  // Render functions for dialogs and controls
  const renderCreateDialog = () => (
    <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
      <DialogTitle>Add New Substrate</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          fullWidth
          margin="normal"
          value={newSubstrate.name}
          onChange={(e) => setNewSubstrate({...newSubstrate, name: e.target.value})}
          required
        />
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          value={newSubstrate.description}
          onChange={(e) => setNewSubstrate({...newSubstrate, description: e.target.value})}
          required
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={newSubstrate.facility}
            onChange={(e) => setNewSubstrate({...newSubstrate, facility: e.target.value})}
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
      <DialogTitle>Edit Substrate</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          fullWidth
          margin="normal"
          value={editingSubstrate?.name || ''}
          onChange={(e) => setEditingSubstrate({...editingSubstrate, name: e.target.value})}
          required
        />
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          value={editingSubstrate?.description || ''}
          onChange={(e) => setEditingSubstrate({...editingSubstrate, description: e.target.value})}
          required
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={editingSubstrate?.facility?._id || ''}
            onChange={(e) => setEditingSubstrate({
              ...editingSubstrate,
              facility: e.target.value
            })}
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdate} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPaginationControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span>Show</span>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          sx={{ mx: 1 }}
          size="small"
        >
          {[10, 25, 50, 100].map(n => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </Select>
        <span>entries</span>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton aria-label="First page"
          onClick={() => handleChangePage(0)}
          disabled={page === 0}
        >
          <FirstPage />
        </IconButton>
        <IconButton aria-label="Previous page"
          onClick={() => handleChangePage(page - 1)}
          disabled={page === 0}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Box sx={{ mx: 2 }}>
          Page {page + 1} of {Math.ceil(filteredSubstrates.length / rowsPerPage)}
        </Box>
        <IconButton aria-label="Next page"
          onClick={() => handleChangePage(page + 1)}
          disabled={page >= Math.ceil(filteredSubstrates.length / rowsPerPage) - 1}
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton aria-label="Last page"
          onClick={() => handleChangePage(Math.ceil(filteredSubstrates.length / rowsPerPage) - 1)}
          disabled={page >= Math.ceil(filteredSubstrates.length / rowsPerPage) - 1}
        >
          <LastPage />
        </IconButton>
      </Box>
    </Box>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="substrate-management">
      <h2>Substrate Management</h2>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search substrates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
          >
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCreateDialogOpen(true)}
        >
          Add New Substrate
        </Button>
        <Button
          variant="outlined"
          onClick={resetSort}
        >
          Reset Sort
        </Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader 
              column="name" 
              label="Name" 
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <SortableTableHeader 
              column="description" 
              label="Description" 
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <SortableTableHeader 
              column="facility.facility_name" 
              label="Facility" 
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedSubstrates.map(substrate => (
            <tr key={substrate._id}>
              <td>{substrate.name || substrate.description || 'N/A'}</td>
              <td>{substrate.description}</td>
              <td>{substrate.facility?.facility_name}</td>
              <td className="action-buttons">
                <Button
                  variant="contained"
                  size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => {
                    setEditingSubstrate({
                      ...substrate,
                      name: substrate.name || substrate.description || ''
                    });
                    setEditDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(substrate._id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {renderPaginationControls()}

      <Box sx={{ mt: 2, color: 'text.secondary' }}>
        Showing {startIndex + 1} to {endIndex} of {sortedData.length} entries
        {searchQuery && ` (filtered from ${substrates.length} total entries)`}
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
    </div>
  );
};

export default SubstrateManagement;